/**
 * Client-side PostHog singleton with a pending-call queue so calls from
 * child `useEffect` hooks that fire before the provider's init effect get
 * replayed once init completes. Session replay input masking below must
 * be re-verified on any posthog-js upgrade.
 */

import posthog from "posthog-js";
import type { PostHog } from "posthog-js";

// Singleton instance tracking
let is_initialized = false;

// Pending call queue — stores calls made before PostHog is initialized
type PendingCall = (ph: PostHog) => void;
const pending_queue: PendingCall[] = [];

/**
 * Substring match used to identify React Router v7 single-fetch turbo-stream
 * decode failures (ORC-388). The exact message originates from
 * `react-router/dist/.../fetchAndDecodeViaTurboStream` and may have minor
 * variations across patch versions, so we use `includes` rather than `===`.
 */
const TURBO_STREAM_DECODE_MESSAGE = "Unable to decode turbo-stream response";

/**
 * Returns true if the given exception value/message looks like a React Router
 * turbo-stream decode failure. Accepts either an array of exception values
 * (PostHog's `$exception_values` shape) or a single message string.
 */
export function is_turbo_stream_decode_error(
  values: unknown,
  message?: unknown,
): boolean {
  if (
    typeof message === "string" &&
    message.includes(TURBO_STREAM_DECODE_MESSAGE)
  ) {
    return true;
  }
  if (Array.isArray(values)) {
    return values.some(
      (v) => typeof v === "string" && v.includes(TURBO_STREAM_DECODE_MESSAGE),
    );
  }
  return false;
}

/**
 * Initialize PostHog on the client side.
 * Safe to call multiple times - only initializes once.
 * After initialization, flushes any pending queued calls.
 *
 * @returns The PostHog instance, or null if in SSR or already initialized
 */
export function init_posthog(): PostHog | null {
  // Guard against SSR
  if (typeof window === "undefined") {
    return null;
  }

  // Prevent re-initialization
  if (is_initialized) {
    return posthog;
  }

  const api_key = import.meta.env.VITE_POSTHOG_API_KEY;
  const api_host = import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com";

  // Skip initialization if no API key
  if (!api_key) {
    console.warn("[PostHog] No API key provided, skipping initialization");
    return null;
  }

  posthog.init(api_key, {
    api_host,
    defaults: "2025-11-30",
    capture_exceptions: true,
    before_send(event) {
      if (!event) return null;
      // Suppress AbortError from React Router fetcher cleanup (ORC-44)
      if (
        event.event === "$exception" &&
        event.properties?.$exception_type === "AbortError"
      ) {
        return null;
      }
      // Suppress turbo-stream decode failures from React Router single-fetch
      // navigation (ORC-388). These fire when a `.data` request returns a
      // non-stream payload (e.g. SSR error page, login redirect HTML, or a
      // truncated edge response). React Router treats them as handled (it
      // falls back to a hard navigation), so they are noise rather than a
      // user-visible bug. A global error listener in PostHogProvider also
      // forces a hard reload as a defensive recovery.
      if (
        event.event === "$exception" &&
        is_turbo_stream_decode_error(
          event.properties?.$exception_values,
          event.properties?.$exception_message,
        )
      ) {
        return null;
      }
      return event;
    },
    disable_session_recording: false,
    respect_dnt: true,
    // Session replay privacy configuration
    session_recording: {
      maskAllInputs: true,
      maskInputOptions: {
        password: true,
        email: true,
      },
    },
    // Opt out in development mode
    opt_out_capturing_by_default: import.meta.env.DEV,
    // Additional configuration for privacy
    mask_all_text: false,
    mask_all_element_attributes: false,
    // Disable autocapture of personally identifiable CSS selectors
    autocapture: {
      css_selector_allowlist: [],
    },
  });

  is_initialized = true;
  console.debug("[PostHog] Initialized successfully");

  // Flush pending queue — replay any calls that were made before init
  if (pending_queue.length > 0) {
    console.debug(`[PostHog] Flushing ${pending_queue.length} pending call(s)`);
    for (const call of pending_queue) {
      call(posthog);
    }
    pending_queue.length = 0;
  }

  return posthog;
}

/**
 * Enqueue a PostHog call. If PostHog is already initialized, the call
 * executes immediately. Otherwise it is queued and replayed when
 * `init_posthog()` completes.
 *
 * This eliminates the race condition where `useEffect` hooks in child
 * components fire before the provider's `useEffect` runs `init_posthog()`.
 *
 * Returns `true` if the call was executed immediately, `false` if queued.
 * During SSR the call is silently dropped and returns `false`.
 */
export function enqueue_posthog_call(call: (ph: PostHog) => void): boolean {
  // Guard against SSR
  if (typeof window === "undefined") {
    return false;
  }

  if (is_initialized) {
    call(posthog);
    return true;
  }

  pending_queue.push(call);
  console.debug("[PostHog] Call queued (not yet initialized)");
  return false;
}

/**
 * Get the PostHog instance.
 * Returns null during SSR or if not initialized.
 */
export function get_posthog(): PostHog | null {
  if (typeof window === "undefined" || !is_initialized) {
    return null;
  }
  return posthog;
}

/**
 * Check if PostHog is initialized and available.
 */
export function is_posthog_ready(): boolean {
  return typeof window !== "undefined" && is_initialized;
}

/**
 * Get the current pending queue length (for testing only).
 * @internal
 */
export function get_pending_queue_length(): number {
  return pending_queue.length;
}

// Re-export the posthog instance for direct access when needed
export { posthog };
